import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  createConciergeSession,
  pingConciergeSession,
  streamConciergeMessage,
} from "../api/services/conciergeAgentService";
import {
  clearActiveJourney,
  getActiveJourney,
  saveActiveJourney,
  type StoredJourneyMessage,
} from "../lib/activeJourneyStorage";
import {
  clearStoredAgentSession,
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import {
  parseSamiJourney,
  stripSamiJourneyBlocksFromContent,
  type SamiJourneyPayload,
} from "../utils/conciergeJourney";
import { buildStepNavigationTarget } from "../utils/journeyStepActions";
import type { AgentContextUsage } from "../types/agentChat";

export interface JourneyChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ActiveJourneyContextValue {
  journey: SamiJourneyPayload | null;
  messages: JourneyChatMessage[];
  userIntent: string;
  currentStepIndex: number;
  completedStepIds: string[];
  hasActiveJourney: boolean;
  awaitingJourneyConfirmation: boolean;
  loading: boolean;
  error: string | null;
  sessionReady: boolean;
  contextUsage: AgentContextUsage | null;
  submitIntent: (text: string) => Promise<string | null>;
  sendMessage: (text: string) => Promise<string | null>;
  markStepStarted: (stepIndex: number) => void;
  executeStep: (stepIndex: number) => void;
  continueJourney: () => void;
  resetJourney: () => void;
}

const ActiveJourneyContext = createContext<ActiveJourneyContextValue | null>(
  null,
);

function toStoredMessages(messages: JourneyChatMessage[]): StoredJourneyMessage[] {
  return messages.map((m) => ({
    ...m,
    timestamp: m.timestamp.toISOString(),
  }));
}

function fromStoredMessages(stored: StoredJourneyMessage[]): JourneyChatMessage[] {
  return stored.map((m) => ({
    ...m,
    timestamp: new Date(m.timestamp),
  }));
}

export function ActiveJourneyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { activeTenantId } = useAuth();

  const [messages, setMessages] = useState<JourneyChatMessage[]>([]);
  const [userIntent, setUserIntent] = useState("");
  const [journey, setJourney] = useState<SamiJourneyPayload | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tenantRef = useRef(activeTenantId);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("concierge", tenantRef.current);
      setSessionId(null);
      setSessionReady(false);
      setContextUsage(null);
      setMessages([]);
      setUserIntent("");
      setJourney(null);
      setCurrentStepIndex(0);
      setCompletedStepIds([]);
    }
    tenantRef.current = activeTenantId;
  }, [activeTenantId]);

  useEffect(() => {
    if (!activeTenantId) {
      setSessionReady(true);
      return;
    }

    const stored = getActiveJourney(activeTenantId);
    if (stored) {
      setUserIntent(stored.userIntent);
      setMessages(fromStoredMessages(stored.messages));
      setJourney(stored.journey ?? null);
      setCurrentStepIndex(stored.currentStepIndex);
      setCompletedStepIds(stored.completedStepIds);
    }

    let cancelled = false;
    async function restoreSession() {
      const agentStored = getStoredAgentSession("concierge", activeTenantId);
      if (!agentStored?.sessionId) {
        if (!cancelled) setSessionReady(true);
        return;
      }
      try {
        const valid = await pingConciergeSession(agentStored.sessionId);
        if (cancelled) return;
        if (valid) {
          setSessionId(agentStored.sessionId);
        } else {
          clearStoredAgentSession("concierge", activeTenantId);
          setSessionId(null);
        }
      } catch {
        if (!cancelled) {
          clearStoredAgentSession("concierge", activeTenantId);
          setSessionId(null);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    }
    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const id = await createConciergeSession();
    setSessionId(id);
    if (activeTenantId) {
      setStoredAgentSession("concierge", activeTenantId, id);
    }
    return id;
  }, [sessionId, activeTenantId]);

  const sendMessage = useCallback(
    async (text: string, options?: { setAsIntent?: boolean }) => {
      if (!text.trim()) return null;
      setError(null);
      setLoading(true);
      const trimmed = text.trim();
      if (options?.setAsIntent) {
        setUserIntent(trimmed);
      }

      const userMsg: JourneyChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: JourneyChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      const nextMessages = [...messages, userMsg, assistantMsg];
      setMessages(nextMessages);

      try {
        const sid = await ensureSession();
        const result = await streamConciergeMessage(sid, trimmed, {
          onTextDelta: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + delta }
                  : m,
              ),
            );
          },
        });
        const parsed = parseSamiJourney(result.response);
        const assistantContent = parsed
          ? stripSamiJourneyBlocksFromContent(result.response)
          : result.response;
        const finalizedAssistant: JourneyChatMessage = {
          id: assistantId,
          role: "assistant",
          content: assistantContent,
          timestamp: new Date(),
        };
        const withAssistant = [
          ...messages,
          userMsg,
          finalizedAssistant,
        ];
        setMessages(withAssistant);
        setContextUsage(result.context_usage ?? null);

        const nextJourney = parsed ?? journey;
        if (parsed) {
          setJourney(parsed);
          setCurrentStepIndex(0);
          setCompletedStepIds([]);
        }

        if (activeTenantId) {
          saveActiveJourney(activeTenantId, {
            userIntent: options?.setAsIntent ? trimmed : userIntent,
            messages: toStoredMessages(withAssistant),
            journey: nextJourney,
            currentStepIndex: parsed ? 0 : currentStepIndex,
            completedStepIds: parsed ? [] : completedStepIds,
            updatedAt: Date.now(),
          });
        }
        return result.response;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to send message");
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev;
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      ensureSession,
      messages,
      journey,
      activeTenantId,
      userIntent,
      currentStepIndex,
      completedStepIds,
    ],
  );

  const submitIntent = useCallback(
    async (text: string) => sendMessage(text, { setAsIntent: true }),
    [sendMessage],
  );

  const markStepStarted = useCallback(
    (stepIndex: number) => {
      if (!journey || stepIndex < 0 || stepIndex >= journey.steps.length) return;
      const stepId = journey.steps[stepIndex].id;
      setCurrentStepIndex(stepIndex);
      setCompletedStepIds((prev) => {
        const next = prev.includes(stepId) ? prev : [...prev, stepId];
        if (activeTenantId) {
          saveActiveJourney(activeTenantId, {
            userIntent,
            messages: toStoredMessages(messages),
            journey,
            currentStepIndex: stepIndex,
            completedStepIds: next,
            updatedAt: Date.now(),
          });
        }
        return next;
      });
    },
    [journey, activeTenantId, userIntent, messages],
  );

  const executeStep = useCallback(
    (stepIndex: number) => {
      if (!journey || stepIndex < 0 || stepIndex >= journey.steps.length) return;
      markStepStarted(stepIndex);
      const target = buildStepNavigationTarget(journey.steps[stepIndex], journey);
      navigate(target);
    },
    [journey, markStepStarted, navigate],
  );

  const continueJourney = useCallback(() => {
    if (!journey) return;
    const nextIndex = Math.min(currentStepIndex + 1, journey.steps.length - 1);
    executeStep(nextIndex);
  }, [journey, currentStepIndex, executeStep]);

  const resetJourney = useCallback(() => {
    setMessages([]);
    setUserIntent("");
    setJourney(null);
    setCurrentStepIndex(0);
    setCompletedStepIds([]);
    setError(null);
    setContextUsage(null);
    if (activeTenantId) {
      clearActiveJourney(activeTenantId);
      clearStoredAgentSession("concierge", activeTenantId);
    }
    setSessionId(null);
  }, [activeTenantId]);

  const hasActiveJourney = Boolean(journey);
  const awaitingJourneyConfirmation =
    messages.some((m) => m.role === "assistant" && m.content.trim().length > 0) &&
    !journey &&
    !loading;

  const value = useMemo(
    (): ActiveJourneyContextValue => ({
      journey,
      messages,
      userIntent,
      currentStepIndex,
      completedStepIds,
      hasActiveJourney,
      awaitingJourneyConfirmation,
      loading,
      error,
      sessionReady,
      contextUsage,
      submitIntent,
      sendMessage,
      markStepStarted,
      executeStep,
      continueJourney,
      resetJourney,
    }),
    [
      journey,
      messages,
      userIntent,
      currentStepIndex,
      completedStepIds,
      hasActiveJourney,
      awaitingJourneyConfirmation,
      loading,
      error,
      sessionReady,
      contextUsage,
      submitIntent,
      sendMessage,
      markStepStarted,
      executeStep,
      continueJourney,
      resetJourney,
    ],
  );

  return (
    <ActiveJourneyContext.Provider value={value}>
      {children}
    </ActiveJourneyContext.Provider>
  );
}

export function useActiveJourney(): ActiveJourneyContextValue {
  const ctx = useContext(ActiveJourneyContext);
  if (!ctx) {
    throw new Error("useActiveJourney must be used within ActiveJourneyProvider");
  }
  return ctx;
}
